<template>
  <v-card id="edit-rebate">
    <v-card-title class="i-card-title-div">
      <v-row class="align-center">
        <v-col cols="12" sm="9">
          <span class="g-bold g-heading-l">{{
            newRebate ? 'New Rebate' : 'Edit Rebate'
          }}</span>
        </v-col>
        <v-col cols="12" sm="3">
          <i-instructions
            instructionKey="data-maintenance/rebates/add-dialog"
          ></i-instructions>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text class="py-8 i-card-content-div">
      <v-alert
        border="left"
        class="mt-1 mb-5 i-alert"
        dense
        text
        outlined
        type="info"
        elevation="1"
        v-if="!newRebate"
      >
        <template v-if="activeRebate"
          >For active Rebates only End Date can be modified</template
        >
        <template v-else
          >For upcoming Rebates Target Type cannot be modified</template
        >
      </v-alert>

      <p class="g-bold g-heading-s g-text-color-black mt-2">Target Type</p>
      <div class="mb-6">
        <v-radio-group
          class="i-radio-group"
          :disabled="!newRebate"
          @change="resetTargetTypeForm"
          v-model="targetType"
        >
          <v-row>
            <v-col cols="12" md="4" class="pb-0">
              <div
                class="item-div-mini mb-0"
                :class="{ 'item-div-active': targetType === BANNER_GROUP }"
              >
                <v-radio
                  label="Banner Group(s)"
                  :value="BANNER_GROUP"
                ></v-radio>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="pb-0">
              <div
                class="item-div-mini mb-0"
                :class="{
                  'item-div-active': targetType === MULTI_STORE_OPERATOR
                }"
              >
                <v-radio
                  label="Multi-Store Operator(s)"
                  :value="MULTI_STORE_OPERATOR"
                ></v-radio>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="pb-0">
              <div
                class="item-div-mini mb-0"
                :class="{ 'item-div-active': targetType === RETAILER_STORE }"
              >
                <v-radio label="Retailer(s)" :value="RETAILER_STORE"></v-radio>
              </div>
            </v-col>
          </v-row>
        </v-radio-group>
      </div>

      <template v-if="targetType === BANNER_GROUP">
        <p class="g-bold g-heading-s g-text-color-black">Banner Group(s)</p>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-4 mb-6"
          clearable
          deletable-chips
          dense
          :disabled="activeRebate"
          :items="targetBannerGroups"
          label="Select Banner Group(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || 'Banner Group(s) required']"
          v-model:search-input="searchInput"
          small-chips
          v-model="targetBannerGroupIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="targetType === MULTI_STORE_OPERATOR">
        <p class="g-bold g-heading-s g-text-color-black">
          Multi-Store Operator(s)
        </p>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-3 mb-6"
          clearable
          deletable-chips
          dense
          :disabled="activeRebate"
          :items="targetMultiStoreOperators"
          label="Select Multi-Store Operator(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || 'Multi-Store Operator(s) required']"
          v-model:search-input="searchInput"
          small-chips
          v-model="targetMultiStoreOperatorIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="targetType === RETAILER_STORE">
        <p class="g-bold g-heading-s g-text-color-black">Retailer(s)</p>
        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-3 mb-6"
          clearable
          deletable-chips
          dense
          :disabled="activeRebate"
          :items="targetStores"
          label="Select Retailer(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || 'Retailer(s) required']"
          v-model:search-input="searchInput"
          small-chips
          v-model="targetStoreIds"
          @change="searchInput = ''"
        ></v-autocomplete>
      </template>

      <template v-if="targetType !== RETAILER_STORE">
        <p class="g-bold g-heading-s g-text-color-black mt-2">
          Rebate Geography
        </p>
        <div class="mb-6">
          <v-radio-group
            class="i-radio-group"
            :disabled="activeRebate"
            v-model="targetGeography"
            @change="resetTargetGeographyForm"
          >
            <v-row>
              <v-col cols="12" md="4" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetGeography === NATIONAL }"
                >
                  <v-radio label="National" :value="NATIONAL"></v-radio>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="pb-0">
                <div
                  class="item-div-mini mb-0"
                  :class="{ 'item-div-active': targetGeography === STATE }"
                >
                  <v-radio label="State(s)" :value="STATE"></v-radio>
                </div>
              </v-col>
            </v-row>
          </v-radio-group>
        </div>

        <template v-if="targetGeography === STATE">
          <p class="g-bold g-heading-s g-text-color-black mt-2">State(s)</p>
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-3 mb-6"
            clearable
            deletable-chips
            dense
            :disabled="activeRebate"
            hide-details
            :items="states"
            label="Select State(s)"
            multiple
            outlined
            :rules="[
              (v) => !!v.length || 'State(s) required for State rebates'
            ]"
            v-model:search-input="searchInput"
            small-chips
            v-model="targetStoreDeliveryAddressStates"
            @change="searchInput = ''"
          ></v-autocomplete>
        </template>
      </template>

      <p class="g-bold g-heading-s g-text-color-black mt-2 mt-md-4">
        Rebate Date Range
        <v-icon
          class="i-tooltip-color pb-1"
          small
          v-tooltip="
            'Starts at 12:00am & Ends at 11:59pm on the submitted dates'
          "
        >
          mdi-help-circle-outline
        </v-icon>
      </p>
      <v-row align="center">
        <v-col cols="12" sm="6">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            :nudge-right="15"
            :nudge-top="30"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                class="mt-0"
                :disabled="activeRebate"
                v-model="startDateDisplay"
                label="Start Date *"
                append-icon="mdi-calendar"
                background-color="#0000000f"
                dense
                outlined
                readonly
                v-on="on"
                :rules="[
                  (v) => !!v || 'Start Date required',
                  validStartSate(startDate, endDate)
                ]"
              ></v-text-field>
            </template>
            <v-date-picker
              :min="minAllowedStartDate"
              v-model="startDate"
              @change="startDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6">
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            :nudge-right="15"
            :nudge-top="30"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-bind="attrs"
                v-model="endDateDisplay"
                label="End Date *"
                outlined
                append-icon="mdi-calendar"
                background-color="#0000000f"
                dense
                readonly
                v-on="on"
                :rules="[
                  (v) => !!v || 'End Date required',
                  validEndDate(startDate, endDate)
                ]"
              ></v-text-field>
            </template>
            <v-date-picker
              :min="minAllowedEndDate"
              v-model="endDate"
              @change="endDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <p class="g-bold g-heading-s g-text-color-black">Rebate %</p>
          <i-numeric-field
            background-color="#0000000f"
            class="mt-4"
            dense
            :disabled="activeRebate"
            label="Rebate % *"
            :max="99.99"
            :min="0"
            outlined
            :precision="2"
            :rules="[(v) => v > 0 || 'Rebate Percentage required']"
            suffix="%"
            text-align-right
            v-model="rebatePercentage"
          >
          </i-numeric-field>
        </v-col>

        <v-col cols="6">
          <p class="g-bold g-heading-s g-text-color-black">Rebate Type</p>
          <v-text-field
            background-color="#0000000f"
            class="mt-4"
            dense
            disabled
            outlined
            label="Rebate Type *"
            value="OFF Invoice"
          >
          </v-text-field>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="i-dialog-actions-div pt-6 justify-end">
      <v-btn rounded text class="i-btn-text-black" @click="$emit('cancel')"
        >Cancel</v-btn
      >
      <v-btn
        rounded
        class="i-btn-block-secondary mr-4"
        :disabled="!validRebate"
        :loading="saving"
        @click="saveRebate"
        >{{ newRebate ? 'Create' : 'Update' }}</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapActions, mapState } from 'vuex'
import { createRebate, updateRebate } from '@/services/rebates'
import iNumericField from '@/components/NumericField'
import FieldValidators from '@/utils/FieldValidators'
import iInstructions from '@/components/Instructions'

const REBATE_FIELDS = {
  rebateId: '',
  endDate: null,
  startDate: null,
  targetType: null,
  targetGeography: null,
  targetBannerGroupIds: [],
  targetMultiStoreOperatorIds: [],
  targetStoreIds: [],
  targetStoreDeliveryAddressStates: [],
  rebatePercentage: '',
  type: 'OFF_INVOICE'
}

const TARGET_GEOGRAPHY = {
  NATIONAL: 'NATIONAL',
  STATE: 'STATE',
  STORE: 'STORE'
}

const TARGET_TYPE = {
  BANNER_GROUP: 'BANNER_GROUP',
  MULTI_STORE_OPERATOR: 'MULTI_STORE_OPERATOR',
  RETAILER_STORE: 'RETAILER_STORE'
}

export default {
  components: {
    iInstructions,
    iNumericField
  },

  data: () => ({
    endDateMenu: false,
    startDateMenu: false,
    searchInput: '',
    originalRebate: {},
    rebateTypes: [{ text: 'OFF Invoice', value: 'OFF_INVOICE' }],
    saving: false,
    states: ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'],
    targetBannerGroups: [],
    targetMultiStoreOperators: [],
    targetStores: [],
    ...FieldValidators,
    ...REBATE_FIELDS,
    ...TARGET_GEOGRAPHY,
    ...TARGET_TYPE
  }),

  props: {
    rebate: Object
  },

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('stores', ['retailerStores']),

    activeRebate() {
      return this.rebate.status === 'ACTIVE'
    },

    beforeEndDate() {
      return this.startDate <= this.endDate
    },

    newRebate() {
      return _.isEmpty(this.rebate)
    },

    changesMade() {
      return !_.isEqual(this.originalRebate, this.createBluePrint())
    },

    endDateDisplay() {
      return this.$options.filters.formatDatePicker(this.endDate)
    },

    startDateDisplay() {
      return this.$options.filters.formatDatePicker(this.startDate)
    },

    minAllowedEndDate() {
      return this.activeRebate
        ? moment().format('YYYY-MM-DD')
        : this.startDate
          ? this.startDate
          : this.minAllowedStartDate
    },

    minAllowedStartDate() {
      return moment().add(1, 'days').format('YYYY-MM-DD')
    },

    validGeography() {
      return (
        this.targetType === this.RETAILER_STORE ||
        this.targetGeography === this.NATIONAL ||
        (this.targetGeography === this.STATE &&
          !!this.targetStoreDeliveryAddressStates.length)
      )
    },

    validTargetType() {
      return (
        (this.targetType === this.BANNER_GROUP &&
          !!this.targetBannerGroupIds.length) ||
        (this.targetType === this.MULTI_STORE_OPERATOR &&
          !!this.targetMultiStoreOperatorIds.length) ||
        (this.targetType === this.RETAILER_STORE &&
          !!this.targetStoreIds.length)
      )
    },

    validRebateDates() {
      return this.startDate && this.endDate >= this.startDate
    },

    validRebatePercentage() {
      return this.rebatePercentage > 0 && this.rebatePercentage < 100
    },

    validRebate() {
      return (
        this.validTargetType &&
        this.validGeography &&
        this.validRebateDates &&
        this.validRebatePercentage &&
        this.changesMade &&
        !!this.type
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    // ***
    // Functions related to help determining if Special has been modified
    // ***
    createBluePrint() {
      return {
        rebateId: this.rebateId,
        endDate: this.endDate,
        startDate: this.startDate,
        targetGeography: this.targetGeography,
        rebatePercentage: this.rebatePercentage,
        type: this.type,
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        targetStoreDeliveryAddressStates: this.targetStoreDeliveryAddressStates,
        targetType: this.targetType
      }
    },

    // ***
    // Functions related to Rebate actions
    // ***

    createRebate(payload) {
      createRebate(this.selectedSupplierId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Rebate has been created!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          let msg = ''
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'SUPPLIER_REBATE_END_DATE_MUST_BE_AFTER_START_DATE':
              msg = 'Rebate End Date must be after Start Date...'
              break

            case 'SUPPLIER_REBATE_END_DATE_MUST_BE_AFTER_TODAY':
              msg = 'Rebate End Date must be after today...'
              break

            case 'SUPPLIER_REBATE_START_DATE_MUST_BE_AFTER_TODAY':
              msg = 'Rebate Start Date must be after today...'
              break

            default:
              msg = 'Rebate could not be created, please try again...'
          }
          this.addNotification({ color: 'error', text: msg })
        })
    },

    copyRebate() {
      this.rebateId = this.rebate.id
      this.endDate = this.rebate.endDate
      this.startDate = this.rebate.startDate
      this.targetGeography = this.rebate.targetGeography
      this.rebatePercentage = this.rebate.rebatePercentage
      this.type = this.rebate.type
      this.targetBannerGroupIds =
        this.rebate.targetBannerGroups?.map(
          (bannerGroup) => bannerGroup.bannerGroupId
        ) || []
      this.targetMultiStoreOperatorIds =
        this.rebate.targetMultiStoreOperators?.map(
          (mso) => mso.multiStoreOperatorId
        ) || []
      this.targetStoreIds =
        this.rebate.targetStores?.map((store) => store.storeId) || []
      this.targetStoreDeliveryAddressStates =
        this.rebate.targetStoreDeliveryAddressStates?.map(
          (state) => state.state
        ) || []
      this.targetType = this.determineTargetType()
    },

    updateRebate(payload) {
      updateRebate(this.selectedSupplierId, this.rebateId, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Rebate has been updated!'
          })
          this.$emit('save')
        })
        .catch((err) => {
          this.saving = false
          let msg = ''
          switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
            case 'SUPPLIER_REBATE_END_DATE_MUST_BE_AFTER_START_DATE':
              msg = 'Rebate End Date must be after Start Date...'
              break

            case 'SUPPLIER_REBATE_END_DATE_MUST_BE_AFTER_TODAY':
              msg = 'Rebate End Date must be after today...'
              break

            case 'SUPPLIER_REBATE_EDIT_NOT_ALLOWED_FOR_PAST':
              msg = 'Rebate Edit is not available for PAST Rebates...'
              break

            default:
              msg = 'Rebate could not be updated, please try again...'
          }
          this.addNotification({ color: 'error', text: msg })
        })
    },

    saveRebate() {
      this.saving = true

      const payload = {
        endDate: this.endDate,
        startDate: this.startDate,
        rebatePercentage: this.rebatePercentage,
        type: this.type,
        targetBannerGroupIds: this.targetBannerGroupIds,
        targetMultiStoreOperatorIds: this.targetMultiStoreOperatorIds,
        targetStoreIds: this.targetStoreIds,
        targetStoreDeliveryAddressStates: this.targetStoreDeliveryAddressStates
      }

      this.newRebate ? this.createRebate(payload) : this.updateRebate(payload)
    },

    // ***
    // Functions related data mapping
    // ***

    mapBannerGroupsList() {
      if (this.bannerGroups?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetBannerGroups = this.bannerGroups
          .map((bannerGroup) => ({
            text: bannerGroup.displayName,
            value: bannerGroup.id
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1))
      } else {
        this.addNotification({
          color: 'secondary',
          text: 'Available Banner Groups loading, please wait...'
        })
      }
    },

    mapMultiStoreOperatorsList() {
      if (this.multiStoreOperators?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetMultiStoreOperators = this.multiStoreOperators
          .map((mso) => ({
            text: mso.displayName,
            value: mso.id
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1))
      } else {
        this.addNotification({
          color: 'secondary',
          text: 'Available Multi-Store Operators loading, please wait...'
        })
      }
    },

    mapStoresList() {
      if (this.retailerStores?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetStores = this.retailerStores
          .map((store) => ({
            text: store.displayName,
            value: store.id
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1))
      } else {
        this.addNotification({
          color: 'secondary',
          text: 'Available Retailer Stores loading, please wait...'
        })
      }
    },

    // ***
    // Helper Functions
    // ***

    determineTargetType() {
      if (this.targetBannerGroupIds.length) return this.BANNER_GROUP
      else if (this.targetMultiStoreOperatorIds.length)
        return this.MULTI_STORE_OPERATOR
      else if (this.targetStoreIds.length) return this.RETAILER_STORE
      else return null
    },

    resetTargetTypeForm() {
      this.targetBannerGroupIds = []
      this.targetMultiStoreOperatorIds = []
      this.targetStoreIds = []

      // Need to reset target geography info as this concept does not apply to Retailer Stores
      if (this.targetType === this.RETAILER_STORE) {
        this.targetGeography = null
        this.resetTargetGeographyForm()
      }
    },

    resetTargetGeographyForm() {
      this.targetStoreDeliveryAddressStates = []
    }
  },

  mounted() {
    this.mapBannerGroupsList()
    this.mapMultiStoreOperatorsList()
    this.mapStoresList()
    if (!this.newRebate) this.copyRebate()
    // Create blue print of rebate so that we can know if changes were made
    this.originalRebate = _.cloneDeep(this.createBluePrint())
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#edit-rebate {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;
  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
}
</style>
